import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from '../store/store';
import DashboardLayout from './DashboardLayout';
import { showOfflineSaveAlertForCategory } from '../utils/offlineAlerts';
import { setFetchedCycles, setLastFetchedForPlantTourId, selectFetchedCycles, selectLastFetchedForPlantTourId, addOfflineData } from '../store/OPRPAndCCPSlice';
import type { OPRPAndCCPCycleData } from '../Services/OPRPAndCCPRecord';
import { startSessionHandler, CATEGORY_TABS_ORDER_QUALITY_INDORE } from '../Services/OPRPAndCCPRecord';
import { QUALITY_INDORE_DEPARTMENT } from '../constants/qualityDepartment';



const OPRPAndCCPRecord: React.FC = () => {
    console.log('OPRPAndCCPRecord component is rendering...');

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { plantTourId, selectedCycle } = useSelector((state: RootState) => state.planTour);
    const { user } = useSelector((state: RootState) => state.user);
    const {
        cycles,
        pendingSync,
        currentCycle: reduxCurrentCycle
    } = useSelector((state: RootState) => state.productMonitoring);

    // Get offline status from app state
    const isOfflineStarted = useSelector((state: RootState) => state.appState.isOfflineStarted);

    // Get OPRP/CCP data from Redux
    const fetchedCycles = useSelector(selectFetchedCycles);
    const lastFetchedForPlantTourId = useSelector(selectLastFetchedForPlantTourId);

    const departmentName = useSelector((state: RootState) => state.planTour?.employeeDetails?.departmentName ?? '');
    const isQualityIndore = departmentName === QUALITY_INDORE_DEPARTMENT;
    const CATEGORY_TABS_ALL = ['OPRP 1', 'OPRP 2', 'OPRP 3', 'OPRP 4', 'CCP'] as const;
    const categoryTabs = (isQualityIndore ? CATEGORY_TABS_ORDER_QUALITY_INDORE : [...CATEGORY_TABS_ALL]) as string[];

    console.log('Redux state loaded successfully:', {
        plantTourId,
        selectedCycle,
        user: user?.Name,
        cycles: cycles.length,
        pendingSync: pendingSync.length,
        isOfflineStarted,
        reduxCurrentCycle,
        codeVerificationCycles: fetchedCycles.length,
        fetchedCyclesData: fetchedCycles
    });
    // Start session form state (category removed; selected in save section as tabs)
    const [startFormData, setStartFormData] = useState({
        executiveName: '',
        lineNo: '',
        locationFrequency: ''
    });

    // Session state
    const [isSessionStarted, setIsSessionStarted] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState('');
    const [expandedCompletedCycles, setExpandedCompletedCycles] = useState<Set<string>>(new Set());

    // Loading state for fetching data
    const [isLoadingCycles, setIsLoadingCycles] = useState(false);

    // Local cycle management
    const [currentCycle, setCurrentCycle] = useState(1);
    const [productFormExpandedByCycle, setProductFormExpandedByCycle] = useState<{ [key: number]: boolean }>({});

    // Category tabs in save section (tab model)
    type ChecklistStatus = 'okay' | 'not-okay' | null;
    interface ChecklistItem { id: string; group: string; label: string; status: ChecklistStatus; remarks: string; }

    type CategoryTab = string;
    const [selectedCategoryTab, setSelectedCategoryTab] = useState<CategoryTab>('OPRP 1');

    const defaultChecklistItems: ChecklistItem[] = [
        { id: 'fe-centre-1', group: 'FE', label: 'Centre 1st Pass', status: null, remarks: '' },
        { id: 'fe-centre-2', group: 'FE', label: 'Centre 2nd Pass', status: null, remarks: '' },
        { id: 'nfe-centre-1', group: 'NFE', label: 'Centre 1st Pass', status: null, remarks: '' },
        { id: 'nfe-centre-2', group: 'NFE', label: 'Centre 2nd Pass', status: null, remarks: '' },
        { id: 'ss-centre-1', group: 'SS', label: 'Centre 1st Pass', status: null, remarks: '' },
        { id: 'ss-centre-2', group: 'SS', label: 'Centre 2nd Pass', status: null, remarks: '' },
        { id: 'md-sensitivity', group: 'M.D. Sensitivity & Rejection in Time', label: 'M.D. Sensitivity & Rejection in Time', status: null, remarks: '' },
    ];

    const defaultChecklistItemsQualityIndore: ChecklistItem[] = [
        { id: 'fe-centre-1', group: 'FE', label: 'Centre 1st Pass', status: null, remarks: '' },
        { id: 'fe-centre-2', group: 'FE', label: 'Centre 2nd Pass', status: null, remarks: '' },
        { id: 'fe-centre-3', group: 'FE', label: 'Centre 3rd Pass', status: null, remarks: '' },
        { id: 'nfe-centre-1', group: 'NFE', label: 'Centre 1st Pass', status: null, remarks: '' },
        { id: 'nfe-centre-2', group: 'NFE', label: 'Centre 2nd Pass', status: null, remarks: '' },
        { id: 'nfe-centre-3', group: 'NFE', label: 'Centre 3rd Pass', status: null, remarks: '' },
        { id: 'ss-centre-1', group: 'SS', label: 'Centre 1st Pass', status: null, remarks: '' },
        { id: 'ss-centre-2', group: 'SS', label: 'Centre 2nd Pass', status: null, remarks: '' },
        { id: 'ss-centre-3', group: 'SS', label: 'Centre 3rd Pass', status: null, remarks: '' },
        { id: 'md-sensitivity', group: 'M.D. Sensitivity & Rejection in Time', label: 'M.D. Sensitivity & Rejection in Time', status: null, remarks: '' },
    ];

    const checklistTemplate = isQualityIndore ? defaultChecklistItemsQualityIndore : defaultChecklistItems;

    const [checklistItemsByCategory, setChecklistItemsByCategory] = useState<Record<string, ChecklistItem[]>>(() => {
        const initial: Record<string, ChecklistItem[]> = {};
        categoryTabs.forEach(tab => {
            initial[tab] = checklistTemplate.map(i => ({ ...i }));
        });
        return initial;
    });

    const checklistItems = checklistItemsByCategory[selectedCategoryTab] || checklistTemplate;

    const handleStatusChange = (itemId: string, status: Exclude<ChecklistStatus, null>) => {
        setChecklistItemsByCategory(prev => ({
            ...prev,
            [selectedCategoryTab]: (prev[selectedCategoryTab] || []).map(item =>
                item.id === itemId ? { ...item, status } : item
            ),
        }));
    };
    const handleRemarksChange = (itemId: string, remarks: string) => {
        setChecklistItemsByCategory(prev => ({
            ...prev,
            [selectedCategoryTab]: (prev[selectedCategoryTab] || []).map(item =>
                item.id === itemId ? { ...item, remarks } : item
            ),
        }));
    };

    const resetChecklistItems = () => {
        const template = isQualityIndore ? defaultChecklistItemsQualityIndore : defaultChecklistItems;
        const next: Record<string, ChecklistItem[]> = {};
        categoryTabs.forEach(tab => {
            next[tab] = template.map(i => ({ ...i }));
        });
        setChecklistItemsByCategory(next);
    };

    useEffect(() => {
        resetChecklistItems();
        setSelectedCategoryTab((t) => (categoryTabs.includes(t) ? t : 'OPRP 1'));
    }, [isQualityIndore]); // eslint-disable-line react-hooks/exhaustive-deps -- replan tabs when plant department is Indore vs others

    const handleStartSession = async () => {
        console.log('Starting code verification session with product:', selectedProduct, 'executive:', startFormData.executiveName);

        try {
            console.log('=== STARTING SESSION ===');
            console.log('Current cycle:', currentCycle, 'Type:', typeof currentCycle);
            console.log('Selected product:', selectedProduct, 'Type:', typeof selectedProduct);
            console.log('Form data:', startFormData);
            console.log('Plant Tour ID:', plantTourId);
            console.log('User:', user?.Name);

            // Ensure shift is available for API save
            try {
                sessionStorage.setItem('shiftValue', selectedCycle || 'Shift 1');
                console.log('Shift value set in sessionStorage:', selectedCycle || 'Shift 1');
            } catch (e) {
                console.warn('Unable to set shiftValue in sessionStorage', e);
            }

            // Use the service to handle start session
            console.log('Using startSessionHandler...');

            console.log('Calling startSessionHandler with parameters:', {
                currentCycle,
                selectedProduct,
                executiveName: startFormData.executiveName,
                lineNo: startFormData.lineNo,
                locationFrequency: startFormData.locationFrequency
            });

            const result = await startSessionHandler(
                currentCycle,
                selectedProduct,
                startFormData.executiveName,
                startFormData.lineNo,
                startFormData.locationFrequency,
                'OPRP 1'
            );

            console.log('startSessionHandler completed successfully:', result);

            // Reset checklist items for new cycle
            resetChecklistItems();
            
            setIsSessionStarted(true);
            setIsExpanded(true);
            console.log('Session started successfully');
        } catch (error) {
            console.error('=== ERROR STARTING SESSION ===');
            console.error('Error details:', error);
            console.error('Error message:', error instanceof Error ? error.message : 'Unknown error');
            console.error('Error stack:', error instanceof Error ? error.stack : 'No stack trace');
            alert('Error starting session. Please try again.');
        }
    };

    // Derive checklist statuses from items (for Redux completed-cycle display)
    const getStatusesFromItems = (items: ChecklistItem[]) => {
        const pick = (group: string, labelKey: string) => items.find(i => i.group === group && i.label === labelKey);
        const val = (it: ChecklistItem | undefined) => it?.status === 'not-okay' ? `Not Okay (${it.remarks || 'No remarks'})` : (it?.status === 'okay' ? 'OK' : 'N/A');
        const fe1 = pick('FE', 'Centre 1st Pass'), fe2 = pick('FE', 'Centre 2nd Pass'), fe3 = pick('FE', 'Centre 3rd Pass');
        const nfe1 = pick('NFE', 'Centre 1st Pass'), nfe2 = pick('NFE', 'Centre 2nd Pass'), nfe3 = pick('NFE', 'Centre 3rd Pass');
        const ss1 = pick('SS', 'Centre 1st Pass'), ss2 = pick('SS', 'Centre 2nd Pass'), ss3 = pick('SS', 'Centre 3rd Pass');
        const mdItem = items.find(i => i.id === 'md-sensitivity' || i.group?.toString().startsWith('M.'));
        return {
            fecentrepass1: val(fe1), fecentrepass2: val(fe2), fecentrepass3: val(fe3),
            nfecentrepass1: val(nfe1), nfecentrepass2: val(nfe2), nfecentrepass3: val(nfe3),
            sscentrepass1: val(ss1), sscentrepass2: val(ss2), sscentrepass3: val(ss3),
            md: val(mdItem),
        };
    };

    const handleSave = async () => {
        console.log('=== HANDLE SAVE STARTED ===');
        console.log('Saving OPRP/CCP data for cycle:', currentCycle, 'all 5 tabs');
        console.log('Current offline status:', isOfflineStarted);

        // At least one tab must have at least one selection (okay or not-okay)
        const hasDataToSave = categoryTabs.some(
            tab => (checklistItemsByCategory[tab] || []).some(ci => ci.status === 'okay' || ci.status === 'not-okay')
        );
        if (!hasDataToSave) {
            alert('No data to save. Please select at least one item in any tab.');
            return;
        }

        // For offline: build proof/remarks from current tab
        const currentTabItems = checklistItemsByCategory[selectedCategoryTab] || [];
        const okays = currentTabItems.filter(ci => ci.status === 'okay').map(ci => `${ci.group} - ${ci.label}`);
        const defects = currentTabItems.filter(ci => ci.status === 'not-okay').map(ci => `${ci.group} - ${ci.label}${ci.remarks ? `: ${ci.remarks}` : ''}`);
        const saveFormData = {
            sku: '',
            machineProof: okays.length > 0 ? `Okays: ${okays.join('; ')}` : '',
            majorDefectsRemarks: defects.length > 0 ? `Defects: ${defects.join('; ')}` : 'No defects',
        };

        try {
            // Check if we're offline
            if (isOfflineStarted) {
                console.log('Processing in offline mode...');

                // Store all 5 tabs for offline sync so when syncing we send 5 API calls (one per tab)
                const offlineRecords = categoryTabs.map((categoryLabel) => {
                    const items = checklistItemsByCategory[categoryLabel] || [];
                    const okays = items.filter(ci => ci.status === 'okay').map(ci => `${ci.group} - ${ci.label}`);
                    const defs = items.filter(ci => ci.status === 'not-okay').map(ci => `${ci.group} - ${ci.label}${ci.remarks ? `: ${ci.remarks}` : ''}`);
                    return {
                        cycleNum: currentCycle.toString(),
                        product: selectedProduct,
                        executiveName: startFormData.executiveName || 'N/A',
                        category: categoryLabel,
                        isQualityIndore,
                        ...getStatusesFromItems(items),
                        proof: okays.length > 0 ? `Okays: ${okays.join('; ')}` : '',
                        remarks: defs.length > 0 ? `Defects: ${defs.join('; ')}` : '',
                    };
                });
                const offlineData = {
                    cycleNo: currentCycle,
                    category: selectedCategoryTab,
                    records: offlineRecords,
                    timestamp: Date.now()
                };
                console.log('Storing offline data in Redux:', offlineData);
                dispatch(addOfflineData(offlineData));
                console.log('Offline data dispatched to Redux store');

                // Debug: Check Redux state after dispatch
                setTimeout(() => {
                    const currentState = (window as any).__REDUX_DEVTOOLS_EXTENSION__?.connect()?.getState();
                    console.log('Redux state after offline data dispatch:', currentState);
                }, 100);

                // Add 5 completed-cycle entries (one per tab) so Summary shows all tabs; offline has only current tab data
                const completedPerTabOffline = categoryTabs.map((categoryLabel) => {
                    const items = checklistItemsByCategory[categoryLabel] || [];
                    const statuses = getStatusesFromItems(items);
                    return {
                    cycleNum: currentCycle.toString(),
                    product: selectedProduct,
                    executiveName: startFormData.executiveName || 'N/A',
                        category: categoryLabel,
                        platform: 'PWA',
                        isQualityIndore,
                        ...statuses,
                        sku: categoryLabel === selectedCategoryTab ? saveFormData.sku : undefined,
                        proof: categoryLabel === selectedCategoryTab ? saveFormData.machineProof : undefined,
                        remarks: categoryLabel === selectedCategoryTab ? saveFormData.majorDefectsRemarks : undefined,
                    };
                });
                dispatch(setFetchedCycles([...fetchedCycles, ...completedPerTabOffline]));

                showOfflineSaveAlertForCategory(currentCycle, 'OPRP and CCP Record');
                setProductFormExpandedByCycle(prev => ({ ...prev, [currentCycle]: false }));
            } else {
                console.log('Processing in online mode... (5 API calls, one per tab)');

                // Pass all 5 tabs' checklist data so service builds 5 payloads and calls API 5 times
                const formDataWithAllTabs = {
                    checklistItemsByCategory: { ...checklistItemsByCategory },
                    categoryTabsOrder: categoryTabs,
                    isQualityIndore,
                };

                const { collectEstimationDataCycleSave, saveSectionApiCall } = await import('../Services/OPRPAndCCPRecord');
                const { savedData } = await collectEstimationDataCycleSave(
                    currentCycle,
                    formDataWithAllTabs,
                    plantTourId || '',
                    user?.Name || '',
                    selectedCycle || 'Shift 1'
                );

                const result = await saveSectionApiCall(savedData);
                if (result.success) {
                    console.log('Data saved successfully to API (5 records, one per tab)');

                    // Add 5 completed-cycle entries to Redux (one per tab) so Summary shows all tabs
                    const completedPerTab = categoryTabs.map((categoryLabel) => {
                        const items = checklistItemsByCategory[categoryLabel] || [];
                        const statuses = getStatusesFromItems(items);
                        return {
                        cycleNum: currentCycle.toString(),
                        product: selectedProduct,
                        executiveName: startFormData.executiveName || 'N/A',
                            category: categoryLabel,
                            platform: 'PWA',
                            isQualityIndore,
                            ...statuses,
                        };
                    });
                    dispatch(setFetchedCycles([...fetchedCycles, ...completedPerTab]));

                    alert('Product saved. Add another product or click "Complete Cycle & Move to Next".');
                } else {
                    throw new Error(result.message);
                }
            }

            // Do NOT advance cycle - user must click "Complete Cycle"
            setProductFormExpandedByCycle(prev => ({ ...prev, [currentCycle]: false }));

        } catch (error) {
            console.error('Error saving data:', error);
            alert('Error saving data. Please try again.');
        }
    };

    // Format date as DD/MM/YYYY
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const formattedDate = `${day}/${month}/${year}`;

    const handleCompleteCycle = () => {
        setCurrentCycle(prev => Math.min(prev + 1, 9));
        setStartFormData({ executiveName: '', lineNo: '', locationFrequency: '' });
        setSelectedProduct('');
        setProductFormExpandedByCycle(prev => ({ ...prev, [currentCycle]: false }));
        setIsSessionStarted(false);
        resetChecklistItems();
    };

    // OPRP adds 5 entries per product (one per tab); unique products = entries / 5
    const currentCycleEntries = fetchedCycles.filter(c => c.cycleNum === currentCycle.toString());
    const tabsPerProduct = categoryTabs.length;
    const currentCycleProductCount = currentCycleEntries.length > 0 ? Math.ceil(currentCycleEntries.length / tabsPerProduct) : 0;
    const showProductSummary = currentCycleProductCount >= 1 && !productFormExpandedByCycle[currentCycle];

    const toggleCompletedCycleExpansion = (cycleNum: string) => {
        setExpandedCompletedCycles(prev => {
            const newSet = new Set(prev);
            if (newSet.has(cycleNum)) {
                newSet.delete(cycleNum);
            } else {
                newSet.add(cycleNum);
            }
            return newSet;
        });
    };

    // Component initialization: always fetch from API (SharePoint/Dynamics) when we have plantTourId so SharePoint completed cycles are shown
    useEffect(() => {
        console.log('=== OPRP AND CCP RECORD COMPONENT MOUNTED ===');
        console.log('Plant Tour ID:', plantTourId);
        console.log('Is Offline:', isOfflineStarted);

        if (plantTourId && !isOfflineStarted) {
            console.log('Plant Tour ID found and not offline, fetching cycle data from API (SharePoint)...');
            fetchCycleDataFromAPI();
        } else if (isOfflineStarted) {
            console.log('In offline mode, skipping API fetch');
        } else if (!plantTourId) {
            console.log('No Plant Tour ID found, cannot fetch cycle data');
        }
    }, [plantTourId, isOfflineStarted]);

    // Set current cycle ONLY on initial mount when we have persisted data from Redux
    // Empty deps [] = runs once on mount only - NOT when user saves a product (which would wrongly advance the cycle)
    const hasInitializedCycleRef = useRef(false);
    useEffect(() => {
        // Skip if already initialized (prevents re-running on hot reload)
        if (hasInitializedCycleRef.current) return;
        
        console.log('=== INITIAL CYCLE SETUP (mount only) ===');
        console.log('fetchedCycles.length:', fetchedCycles.length);
        
        if (fetchedCycles.length === 0) {
            setCurrentCycle(1);
            return;
        }
        
        hasInitializedCycleRef.current = true;
        const maxCycleNumber = Math.max(...fetchedCycles.map(cycle => parseInt(cycle.cycleNum) || 1));
        setCurrentCycle(maxCycleNumber + 1);
        console.log(`Mount with persisted data: ${fetchedCycles.length} entries. Setting current to: ${maxCycleNumber + 1}`);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- only on mount for persisted data

    // Reset checklist items whenever currentCycle changes
    useEffect(() => {
        console.log('Current cycle changed to:', currentCycle, '- resetting checklist items');
        resetChecklistItems();
    }, [currentCycle]);

    // Function to fetch cycle data from API
    const fetchCycleDataFromAPI = async () => {
        try {
            setIsLoadingCycles(true);
            console.log('=== FETCHING CYCLE DATA FROM API ===');
            console.log('Plant Tour ID:', plantTourId);
            console.log('Is Offline:', isOfflineStarted);

            if (!plantTourId) {
                console.log('No Plant Tour ID available, skipping fetch');
                return;
            }

            const { fetchCycleData } = await import('../Services/OPRPAndCCPRecord');
            console.log('fetchCycleData function imported successfully');

            const fetchedData = await fetchCycleData(plantTourId);
            console.log('API Response - Fetched cycles from API (SharePoint):', fetchedData);

            const apiCycles = Array.isArray(fetchedData) ? (fetchedData as OPRPAndCCPCycleData[]) : [];
            const isNewPlantTour = plantTourId !== lastFetchedForPlantTourId;

            if (isNewPlantTour) {
                // New plant tour ID: use only API result so we don't show cycles from the previous tour
                dispatch(setFetchedCycles(apiCycles));
                dispatch(setLastFetchedForPlantTourId(plantTourId || null));
                if (apiCycles.length > 0) {
                    const maxCycle = Math.max(...apiCycles.map(c => parseInt(c.cycleNum) || 1));
                    setCurrentCycle(maxCycle + 1);
                } else {
                    setCurrentCycle(1);
                }
                console.log('New plant tour: showing', apiCycles.length, 'cycles from API (no merge with previous tour).');
            } else {
                // Same plant tour: merge API cycles with any local-only (e.g. just saved this session)
                const key = (r: OPRPAndCCPCycleData) => `${r.cycleNum}-${(r.category || '').trim()}`;
                const apiKeys = new Set(apiCycles.map(key));
                const localOnly = fetchedCycles.filter((r) => !apiKeys.has(key(r)));
                const merged = [...apiCycles, ...localOnly];
                dispatch(setFetchedCycles(merged));
                if (merged.length > 0) {
                    const maxCycle = Math.max(...merged.map(c => parseInt(c.cycleNum) || 1));
                    setCurrentCycle(maxCycle + 1);
                }
                if (apiCycles.length > 0) {
                    console.log('Successfully fetched', apiCycles.length, 'cycles from SharePoint; merged total:', merged.length);
                } else {
                    console.log('No cycles from API for this plant tour; showing', merged.length, 'local only');
                }
            }
        } catch (error) {
            console.error('Error fetching OPRP/CCP cycle data:', error);
            console.error('Error details:', {
                message: error instanceof Error ? error.message : 'Unknown error',
                stack: error instanceof Error ? error.stack : 'No stack trace'
            });
            // On error, keep existing Redux data so we don't lose local cycles
            if (fetchedCycles.length === 0) dispatch(setFetchedCycles([]));
        } finally {
            setIsLoadingCycles(false);
        }
    };

    return (
        <DashboardLayout>
            {/* Header Section */}
            <div className="light-surface bg-white px-3 sm:px-4 md:px-6 py-3 sm:py-4 mb-4 sm:mb-6 border-b border-gray-200 w-full">
                <div className="flex items-center justify-between gap-2">
                    {/* Back Button */}
                    <button
                        onClick={() => {
                            console.log('Back button clicked, navigating to home page');
                            navigate('/');
                        }}
                        className="flex items-center text-blue-600 hover:text-blue-800 transition-colors flex-shrink-0"
                    >
                        <span className="text-lg mr-1">&lt;</span>
                        <span className="font-medium text-sm sm:text-base">Back</span>
                    </button>

                    {/* Plant Tour ID */}
                    <div className="text-right min-w-0 flex-1">
                        <span className="text-gray-700 text-sm sm:text-base">Plant Tour ID: </span>
                        <span className="text-blue-600 font-medium text-sm sm:text-base break-all truncate">{plantTourId || 'N/A'}</span>
                    </div>
                </div>
            </div>

            {/* OPRP and CCP Record Header */}
            <div className="bg-gray-100 px-3 sm:px-4 md:px-6 py-3 sm:py-4 mb-4 sm:mb-6 rounded-lg w-full">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                        <h1 className="text-base sm:text-lg md:text-xl font-bold text-gray-800">OPRP and CCP Record</h1>
                        <div className="flex items-center gap-2 sm:gap-4">
                            <div className="bg-gray-200 rounded-full px-2 sm:px-3 py-1 flex items-center gap-1 sm:gap-2">
                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                <span className="text-xs sm:text-sm font-medium text-gray-700">{selectedCycle || 'Shift 1'}</span>
                            </div>
                            <span className="text-xs sm:text-sm text-gray-600">{formattedDate}</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={fetchCycleDataFromAPI}
                            disabled={isLoadingCycles}
                            className="text-blue-600 hover:text-blue-800 transition-colors flex items-center gap-1"
                        >
                            <svg className={`w-4 h-4 ${isLoadingCycles ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            {/* <span className="text-sm">Refresh</span> */}
                        </button>
                    </div>
                </div>
            </div>

            {/* Completed Cycles Display - Summary: per cycle, per product, five category cards (OPRP 1–4, CCP) */}
            {fetchedCycles.length > 0 && (() => {
                // Group by cycle number
                const byCycle = fetchedCycles.reduce<Record<string, OPRPAndCCPCycleData[]>>((acc, r) => {
                    const n = r.cycleNum;
                    if (!acc[n]) acc[n] = [];
                    acc[n].push(r);
                    return acc;
                }, {});
                const cycleNumbers = Object.keys(byCycle).sort((a, b) => parseInt(a, 10) - parseInt(b, 10));

                // Within each cycle, group records by product (one row per category tab — 5 default, 3 for Quality Indore)
                const byCycleByProduct = (cycleNum: string): { productName: string; records: OPRPAndCCPCycleData[] }[] => {
                    const records = byCycle[cycleNum] || [];
                    const byProduct = records.reduce<Record<string, OPRPAndCCPCycleData[]>>((acc, r) => {
                        const key = `${(r.product || 'N/A').trim()}|${(r.executiveName || 'N/A').trim()}`;
                        if (!acc[key]) acc[key] = [];
                        acc[key].push(r);
                        return acc;
                    }, {});
                    return Object.entries(byProduct).map(([, productRecords]) => ({
                        productName: productRecords[0]?.product || 'N/A',
                        records: productRecords,
                    }));
                };

                // Derive checklist values from record (API fields) or from proof/remarks (locally saved)
                const getChecklistValues = (record: OPRPAndCCPCycleData | undefined | null): Record<string, string> => {
                    const out: Record<string, string> = {
                        fecentrepass1: 'N/A', fecentrepass2: 'N/A', fecentrepass3: 'N/A',
                        nfecentrepass1: 'N/A', nfecentrepass2: 'N/A', nfecentrepass3: 'N/A',
                        sscentrepass1: 'N/A', sscentrepass2: 'N/A', sscentrepass3: 'N/A',
                        md: 'N/A',
                    };
                    if (!record) return out;
                    if (record.fecentrepass1 != null || record.fecentrepass2 != null || record.fecentrepass3 != null || record.md != null) {
                        out.fecentrepass1 = record.fecentrepass1 ?? 'N/A';
                        out.fecentrepass2 = record.fecentrepass2 ?? 'N/A';
                        out.fecentrepass3 = record.fecentrepass3 ?? 'N/A';
                        out.nfecentrepass1 = record.nfecentrepass1 ?? 'N/A';
                        out.nfecentrepass2 = record.nfecentrepass2 ?? 'N/A';
                        out.nfecentrepass3 = record.nfecentrepass3 ?? 'N/A';
                        out.sscentrepass1 = record.sscentrepass1 ?? 'N/A';
                        out.sscentrepass2 = record.sscentrepass2 ?? 'N/A';
                        out.sscentrepass3 = record.sscentrepass3 ?? 'N/A';
                        out.md = record.md ?? 'N/A';
                        return out;
                    }
                    const parseList = (s?: string | null) => {
                        if (!s) return [] as string[];
                        const cleaned = s.replace(/^Okays:\s*/i, '').replace(/^Defects:\s*/i, '');
                        return cleaned.split(';').map(x => x.trim()).filter(Boolean);
                    };
                    const okays = new Set(parseList(record.proof as string));
                    const defectsArr = parseList(record.remarks as string);
                    const defectsMap = new Map<string, string>();
                    defectsArr.forEach((d: string) => {
                        const [k, v] = d.split(':').map((x: string) => x?.trim());
                        if (k) defectsMap.set(k, v || 'No remarks');
                    });
                    const statusFor = (group: string, label: string) => {
                        const key = `${group} - ${label}`;
                        if (okays.has(key)) return 'OK';
                        if (defectsMap.has(key)) return `Not Okay (${defectsMap.get(key)})`;
                        return 'N/A';
                    };
                    out.fecentrepass1 = statusFor('FE', 'Centre 1st Pass');
                    out.fecentrepass2 = statusFor('FE', 'Centre 2nd Pass');
                    out.fecentrepass3 = statusFor('FE', 'Centre 3rd Pass');
                    out.nfecentrepass1 = statusFor('NFE', 'Centre 1st Pass');
                    out.nfecentrepass2 = statusFor('NFE', 'Centre 2nd Pass');
                    out.nfecentrepass3 = statusFor('NFE', 'Centre 3rd Pass');
                    out.sscentrepass1 = statusFor('SS', 'Centre 1st Pass');
                    out.sscentrepass2 = statusFor('SS', 'Centre 2nd Pass');
                    out.sscentrepass3 = statusFor('SS', 'Centre 3rd Pass');
                    out.md = statusFor('M.D. Sensitivity & Rejection in Time', 'M.D. Sensitivity & Rejection in Time');
                    return out;
                };

                const getDisplayValue = (record: OPRPAndCCPCycleData | undefined, key: keyof ReturnType<typeof getChecklistValues>): string => {
                    const values = getChecklistValues(record);
                    const v = values[key];
                    return v === 'OK' ? 'OK' : (v && typeof v === 'string' ? v : 'N/A');
                };

                const getRecordByCategory = (records: OPRPAndCCPCycleData[], category: string): OPRPAndCCPCycleData | undefined =>
                    records.find(r => (r.category || '').trim() === category);

                return (
                <div className="space-y-4 mb-6">
                        {cycleNumbers.map((cycleNum) => {
                            const records = byCycle[cycleNum] || [];
                            const productsInCycle = byCycleByProduct(cycleNum);
                            const productCount = productsInCycle.length;

                        return (
                                <div key={cycleNum} className="light-surface bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 md:p-6 w-full">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h2 className="text-base sm:text-lg font-bold text-gray-800">Cycle {cycleNum}</h2>
                                            <span className="text-xs text-gray-600">({productCount} product{productCount !== 1 ? 's' : ''})</span>
                                            {records.length > 0 && (() => {
                                                const platform = records[0].platform;
                                                const isWeb = platform && String(platform).toLowerCase() === 'web';
                                                return (
                                                    <span className={`text-xs font-medium px-2 py-0.5 rounded ${isWeb ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                                                        {isWeb ? 'Web' : 'PWA'}
                                                    </span>
                                                );
                                            })()}
                                    </div>
                                    <div className="flex items-center gap-2">
                                            <svg className="w-4 h-4 text-green-500 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        <svg
                                                className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-500 cursor-pointer transition-transform ${expandedCompletedCycles.has(cycleNum) ? 'rotate-180' : ''}`}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                                onClick={() => toggleCompletedCycleExpansion(cycleNum)}
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </div>
                                </div>

                                    {expandedCompletedCycles.has(cycleNum) && (
                                        <div className="border border-gray-200 rounded-lg p-3 sm:p-4 bg-white space-y-6">
                                            <div className="mb-4">
                                                <h3 className="text-base sm:text-lg font-bold text-gray-800">Cycle {cycleNum}</h3>
                                                <div className="text-lg font-bold text-red-600 mt-1">Summary</div>
                                            </div>

                                            {/* Per-category blocks: OPRP 1 Summary, OPRP 2 Summary, ... CCP Summary */}
                                            {categoryTabs.map((categoryLabel) => {
                                                const summaryTitle = categoryLabel === 'CCP' ? 'CCP Summary' : `${categoryLabel} Summary`;
                                                return (
                                                    <div key={categoryLabel} className="light-surface bg-white border border-gray-200 rounded-lg overflow-hidden">
                                                        <div className="px-3 py-2 bg-gray-100 border-b border-gray-200 font-semibold text-gray-800 text-sm sm:text-base">
                                                            {summaryTitle}
                                                        </div>
                                                        <div className="divide-y divide-gray-200">
                                                            {productsInCycle.map(({ productName, records: productRecords }, productIdx) => {
                                                                const record = getRecordByCategory(productRecords, categoryLabel);
                                                                const lineNo = record?.lineNo ?? record?.sku ?? 'N/A';
                                                                const location = record?.location ?? 'N/A';
                                                                return (
                                                                    <div key={`${categoryLabel}-${productIdx}`} className="p-3">
                                                                        <div className="text-xs font-semibold text-gray-500 mb-2">{categoryLabel} - Row {productIdx + 1}</div>
                                                                        <div className="overflow-x-auto">
                                                                            <table className="w-full border-collapse border border-gray-300 text-left text-xs sm:text-sm">
                                                                                <thead>
                                                                                    <tr className="bg-gray-50">
                                                                                        <th className="border border-gray-300 px-2 py-2 font-medium">Product</th>
                                                                                        <th className="border border-gray-300 px-2 py-2 font-medium">Executive Name</th>
                                                                                        <th className="border border-gray-300 px-2 py-2 font-medium">Line No</th>
                                                                                        <th className="border border-gray-300 px-2 py-2 font-medium">Location &amp; Frequency</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    <tr>
                                                                                        <td className="border border-gray-300 px-2 py-2">{record?.product ?? productName ?? 'N/A'}</td>
                                                                                        <td className="border border-gray-300 px-2 py-2">{record?.executiveName ?? 'N/A'}</td>
                                                                                        <td className="border border-gray-300 px-2 py-2">{lineNo}</td>
                                                                                        <td className="border border-gray-300 px-2 py-2">{location}</td>
                                                                                    </tr>
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                        <div className="mt-2 overflow-x-auto">
                                                                            <table className="w-full border-collapse border border-gray-300 text-center text-xs sm:text-sm">
                                                                                <thead>
                                                                                    <tr className="bg-gray-50">
                                                                                        <th className="border border-gray-300 px-2 py-2" colSpan={isQualityIndore ? 3 : 2}>FE</th>
                                                                                        <th className="border border-gray-300 px-2 py-2" colSpan={isQualityIndore ? 3 : 2}>NFE</th>
                                                                                        <th className="border border-gray-300 px-2 py-2" colSpan={isQualityIndore ? 3 : 2}>SS</th>
                                                                                        <th className="border border-gray-300 px-2 py-2">M.D.</th>
                                                                                    </tr>
                                                                                    <tr className="bg-gray-50">
                                                                                        <th className="border border-gray-300 px-2 py-2">1st</th>
                                                                                        <th className="border border-gray-300 px-2 py-2">2nd</th>
                                                                                        {isQualityIndore && <th className="border border-gray-300 px-2 py-2">3rd</th>}
                                                                                        <th className="border border-gray-300 px-2 py-2">1st</th>
                                                                                        <th className="border border-gray-300 px-2 py-2">2nd</th>
                                                                                        {isQualityIndore && <th className="border border-gray-300 px-2 py-2">3rd</th>}
                                                                                        <th className="border border-gray-300 px-2 py-2">1st</th>
                                                                                        <th className="border border-gray-300 px-2 py-2">2nd</th>
                                                                                        {isQualityIndore && <th className="border border-gray-300 px-2 py-2">3rd</th>}
                                                                                        <th className="border border-gray-300 px-2 py-2">&nbsp;</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    <tr>
                                                                                        <td className="border border-gray-300 px-2 py-2">{getDisplayValue(record, 'fecentrepass1')}</td>
                                                                                        <td className="border border-gray-300 px-2 py-2">{getDisplayValue(record, 'fecentrepass2')}</td>
                                                                                        {isQualityIndore && <td className="border border-gray-300 px-2 py-2">{getDisplayValue(record, 'fecentrepass3')}</td>}
                                                                                        <td className="border border-gray-300 px-2 py-2">{getDisplayValue(record, 'nfecentrepass1')}</td>
                                                                                        <td className="border border-gray-300 px-2 py-2">{getDisplayValue(record, 'nfecentrepass2')}</td>
                                                                                        {isQualityIndore && <td className="border border-gray-300 px-2 py-2">{getDisplayValue(record, 'nfecentrepass3')}</td>}
                                                                                        <td className="border border-gray-300 px-2 py-2">{getDisplayValue(record, 'sscentrepass1')}</td>
                                                                                        <td className="border border-gray-300 px-2 py-2">{getDisplayValue(record, 'sscentrepass2')}</td>
                                                                                        {isQualityIndore && <td className="border border-gray-300 px-2 py-2">{getDisplayValue(record, 'sscentrepass3')}</td>}
                                                                                        <td className="border border-gray-300 px-2 py-2" colSpan={1}>{getDisplayValue(record, 'md')}</td>
                                                                                    </tr>
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                            </div>
                        );
                    })}
                </div>
                );
            })()}



            {/* Main Content - Current Cycle Section */}
            <div className="light-surface bg-white rounded-lg shadow-sm border p-3 sm:p-4 md:p-6 w-full">
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                        <h2 className="text-base sm:text-lg font-bold text-gray-800">Cycle {currentCycle}</h2>
                    <svg
                        className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-500 cursor-pointer transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        onClick={() => setIsExpanded(!isExpanded)}
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                </div>

                {/* Completed product(s) summary when 1+ products saved */}
                {showProductSummary ? (
                    <div className="mb-4">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-green-700 font-semibold">
                                    {currentCycleProductCount} product(s) saved in Cycle {currentCycle}
                                </span>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-3">
                                <button
                                    onClick={() => {
                                        setProductFormExpandedByCycle(prev => ({ ...prev, [currentCycle]: true }));
                                        setIsSessionStarted(false);
                                        setSelectedProduct('');
                                        setStartFormData({ executiveName: '', lineNo: '', locationFrequency: '' });
                                        resetChecklistItems();
                                    }}
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium"
                                >
                                    Add New Product
                                </button>
                                <button
                                    onClick={handleCompleteCycle}
                                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium"
                                >
                                    Complete Cycle & Move to Next
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                <>
                {/* Start Session Form */}
                {!isSessionStarted && (
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                            {/* Product Field */}
                            {/* <div>
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Product</label>
                                <select
                                    className="w-full border border-gray-300 rounded-md px-2 sm:px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    value={selectedProduct}
                                    onChange={(e) => setSelectedProduct(e.target.value)}
                                >
                                    <option value="">Select Product</option>
                                    <option value="speciality_sauces">Speciality Sauces</option>
                                    <option value="zesty_wasabi">Zesty Wasabi</option>
                                    <option value="mayonnaise">Mayonnaise</option>
                                    <option value="sandwich_spread">Sandwich Spread</option>
                                    <option value="indian_chutneys">Indian Chutneys</option>
                                </select>
                            </div> */}
                             <div>
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Product Name</label>
                                <input
                                    type="text"
                                    value={selectedProduct || ''}
                                    onChange={(e) => setSelectedProduct(e.target.value)}
                                    className="w-full border border-gray-300 rounded-md px-2 sm:px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Enter Product Name"
                                />
                            </div>

                            {/* Executive Name Field */}
                            <div>
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Executive Name</label>
                                <input
                                    type="text"
                                    value={startFormData.executiveName || ''}
                                    onChange={(e) => setStartFormData(prev => ({ ...prev, executiveName: e.target.value }))}
                                    className="w-full border border-gray-300 rounded-md px-2 sm:px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Enter executive name"
                                />
                            </div>

                            {/* Line No */}
                            <div>
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Line No</label>
                                <input
                                    type="text"
                                    value={startFormData.lineNo}
                                    onChange={(e) => setStartFormData(prev => ({ ...prev, lineNo: e.target.value }))}
                                    className="w-full border border-gray-300 rounded-md px-2 sm:px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Enter Line No"
                                />
                            </div>

                            {/* Location & Frequency */}
                            <div>
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Location & Frequency</label>
                                <input
                                    type="text"
                                    value={startFormData.locationFrequency}
                                    onChange={(e) => setStartFormData(prev => ({ ...prev, locationFrequency: e.target.value }))}
                                    className="w-full border border-gray-300 rounded-md px-2 sm:px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Enter Location & Frequency"
                                />
                            </div>
                        </div>

                        {/* Start Session Button */}
                        <div className="flex justify-end mt-4 sm:mt-6 md:mt-8">
                            <button
                                onClick={handleStartSession}
                                className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-md font-medium text-sm sm:text-base transition-colors"
                            >
                                Start Session
                            </button>
                        </div>
                    </div>
                )}

                {/* Form Content - Save Section */}
                {isSessionStarted && (
                    <div className="space-y-4">
                        {/* Header Summary */}
                        <div className="border rounded-lg overflow-hidden">
                            <div className="grid grid-cols-1 sm:grid-cols-4 bg-blue-50 px-3 sm:px-4 py-3">
                                <div className="mb-2 sm:mb-0">
                                    <div className="text-xs text-gray-600">Product</div>
                                    <div className="text-sm font-semibold text-gray-800 truncate">{selectedProduct}</div>
                                </div>
                                <div className="mb-2 sm:mb-0">
                                    <div className="text-xs text-gray-600">Executive Name</div>
                                    <div className="text-sm font-semibold text-gray-800 truncate">{startFormData.executiveName || 'N/A'}</div>
                                </div>
                                <div className="mb-2 sm:mb-0">
                                    <div className="text-xs text-gray-600">Line No</div>
                                    <div className="text-sm font-semibold text-gray-800 truncate">{startFormData.lineNo || 'N/A'}</div>
                                </div>
                                <div className="mb-2 sm:mb-0">
                                    <div className="text-xs text-gray-600">Location & Frequency</div>
                                    <div className="text-sm font-semibold text-gray-800 truncate">{startFormData.locationFrequency || 'N/A'}</div>
                                </div>
                                </div>
                            </div>

                        {/* Category tabs (OPRP 1, OPRP 2, OPRP 3, OPRP 4, CCP) */}
                        <div className="flex flex-wrap gap-1 sm:gap-2 border-b border-gray-200 pb-2">
                            {categoryTabs.map((tab) => (
                                <button
                                    key={tab}
                                    type="button"
                                    onClick={() => setSelectedCategoryTab(tab)}
                                    className={`px-3 py-2 rounded-t-md text-sm font-medium border transition-colors ${
                                        selectedCategoryTab === tab
                                            ? 'bg-blue-600 text-white border-blue-600'
                                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>

                        {/* Checklist Groups (for selected tab) */}
                        {/* FE */}
                        <div className="border rounded-lg p-3 sm:p-4">
                            <div className="font-semibold text-gray-800 mb-2">FE</div>
                            {checklistItems.filter(i => i.group === 'FE').map(item => (
                                <div key={item.id} className="border border-gray-200 rounded-lg p-3 sm:p-4 bg-white mb-3 last:mb-0">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm sm:text-base font-medium text-gray-800">{item.label}</span>
                                        <div className="flex items-center gap-3 sm:gap-4">
                                            <button
                                                type="button"
                                                onClick={() => handleStatusChange(item.id, 'not-okay')}
                                                className={`px-3 py-1 rounded border ${item.status === 'not-okay' ? 'bg-red-100 border-red-600 text-red-600' : 'border-red-400 text-red-500'}`}
                                            >
                                                Not Okay
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleStatusChange(item.id, 'okay')}
                                                className={`px-3 py-1 rounded border ${item.status === 'okay' ? 'bg-green-100 border-green-600 text-green-600' : 'border-green-400 text-green-500'}`}
                                            >
                                                Okay
                                            </button>
                                        </div>
                                    </div>
                                    {item.status === 'not-okay' && (
                                        <div className="mt-4 pt-4 border-t border-gray-200">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Major Defects and Remarks</label>
                                            <textarea
                                                value={item.remarks}
                                                onChange={(e) => handleRemarksChange(item.id, e.target.value)}
                                                placeholder="Type Here..."
                                                className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                                rows={4}
                                            />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* NFE */}
                        <div className="border rounded-lg p-3 sm:p-4">
                            <div className="font-semibold text-gray-800 mb-2">NFE</div>
                            {checklistItems.filter(i => i.group === 'NFE').map(item => (
                                <div key={item.id} className="border border-gray-200 rounded-lg p-3 sm:p-4 bg-white mb-3 last:mb-0">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm sm:text-base font-medium text-gray-800">{item.label}</span>
                                        <div className="flex items-center gap-3 sm:gap-4">
                                            <button
                                                type="button"
                                                onClick={() => handleStatusChange(item.id, 'not-okay')}
                                                className={`px-3 py-1 rounded border ${item.status === 'not-okay' ? 'bg-red-100 border-red-600 text-red-600' : 'border-red-400 text-red-500'}`}
                                            >
                                                Not Okay
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleStatusChange(item.id, 'okay')}
                                                className={`px-3 py-1 rounded border ${item.status === 'okay' ? 'bg-green-100 border-green-600 text-green-600' : 'border-green-400 text-green-500'}`}
                                            >
                                                Okay
                                            </button>
                                        </div>
                                    </div>
                                    {item.status === 'not-okay' && (
                                        <div className="mt-4 pt-4 border-t border-gray-200">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Major Defects and Remarks</label>
                                            <textarea
                                                value={item.remarks}
                                                onChange={(e) => handleRemarksChange(item.id, e.target.value)}
                                                placeholder="Type Here..."
                                                className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                                rows={4}
                                            />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* SS */}
                        <div className="border rounded-lg p-3 sm:p-4">
                            <div className="font-semibold text-gray-800 mb-2">SS</div>
                            {checklistItems.filter(i => i.group === 'SS').map(item => (
                                <div key={item.id} className="border border-gray-200 rounded-lg p-3 sm:p-4 bg-white mb-3 last:mb-0">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm sm:text-base font-medium text-gray-800">{item.label}</span>
                                        <div className="flex items-center gap-3 sm:gap-4">
                                            <button
                                                type="button"
                                                onClick={() => handleStatusChange(item.id, 'not-okay')}
                                                className={`px-3 py-1 rounded border ${item.status === 'not-okay' ? 'bg-red-100 border-red-600 text-red-600' : 'border-red-400 text-red-500'}`}
                                            >
                                                Not Okay
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleStatusChange(item.id, 'okay')}
                                                className={`px-3 py-1 rounded border ${item.status === 'okay' ? 'bg-green-100 border-green-600 text-green-600' : 'border-green-400 text-green-500'}`}
                                            >
                                                Okay
                                            </button>
                                        </div>
                                    </div>
                                    {item.status === 'not-okay' && (
                                        <div className="mt-4 pt-4 border-t border-gray-200">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Major Defects and Remarks</label>
                                            <textarea
                                                value={item.remarks}
                                                onChange={(e) => handleRemarksChange(item.id, e.target.value)}
                                                placeholder="Type Here..."
                                                className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                                rows={4}
                                            />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                         {/* M.D. Sensitivity & Rejection in Time */}
                         <div className="border rounded-lg p-3 sm:p-4">
                             <div className="font-semibold text-gray-800 mb-2">M.D. Sensitivity & Rejection in Time</div>
                             {checklistItems.filter(i => i.id === 'md-sensitivity').map(item => (
                                 <div key={item.id} className="border border-gray-200 rounded-lg p-3 sm:p-4 bg-white mb-3 last:mb-0">
                                     <div className="flex items-center justify-between">
                                         <span className="text-sm sm:text-base font-medium text-gray-800">{item.label}</span>
                                         <div className="flex items-center gap-3 sm:gap-4">
                                             <button
                                                 type="button"
                                                 onClick={() => handleStatusChange(item.id, 'not-okay')}
                                                 className={`px-3 py-1 rounded border ${item.status === 'not-okay' ? 'bg-red-100 border-red-600 text-red-600' : 'border-red-400 text-red-500'}`}
                                             >
                                                 Not Okay
                                             </button>
                                             <button
                                                 type="button"
                                                 onClick={() => handleStatusChange(item.id, 'okay')}
                                                 className={`px-3 py-1 rounded border ${item.status === 'okay' ? 'bg-green-100 border-green-600 text-green-600' : 'border-green-400 text-green-500'}`}
                                             >
                                                 Okay
                                             </button>
                                         </div>
                                     </div>
                                     {item.status === 'not-okay' && (
                                         <div className="mt-4 pt-4 border-t border-gray-200">
                                             <label className="block text-sm font-medium text-gray-700 mb-2">Major Defects and Remarks</label>
                                             <textarea
                                                 value={item.remarks}
                                                 onChange={(e) => handleRemarksChange(item.id, e.target.value)}
                                                 placeholder="Type Here..."
                                                 className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                                 rows={4}
                                             />
                                         </div>
                                     )}
                                 </div>
                             ))}
                         </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end space-x-4">
                            <button
                                onClick={() => setIsSessionStarted(false)}
                                className="px-6 py-2 border border-blue-500 text-blue-500 rounded-md hover:bg-blue-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                            >
                                Save Product Checklist
                            </button>
                        </div>
                    </div>
                )}
                </>
                )}
            </div>
        </DashboardLayout>
    );
};

export default OPRPAndCCPRecord;
